import { PlusIcon } from '@shopify/polaris-icons';
import { Form, TextField } from '../components/forms';
/**
 * ACCEPT INVITATION PAGE
 *
 * Accept an organization invitation and create account or sign in.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Box,
  Button,
  Divider,
  Spinner,
} from '@shopify/polaris';
import { useAuth } from '../App';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

// Password requirements
const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_REQUIREMENTS = [
  { test: (p: string) => p.length >= PASSWORD_MIN_LENGTH, message: `At least ${PASSWORD_MIN_LENGTH} characters` },
  { test: (p: string) => /[A-Z]/.test(p), message: 'One uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p), message: 'One lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p), message: 'One number' },
];

interface InvitationInfo {
  email: string;
  organizationName: string;
  role: string;
  invitedBy: string;
  hasExistingAccount: boolean;
}

export default function AcceptInvitation() {
  const { token: pathToken } = useParams<{ token: string }>();
  const token = pathToken || new URLSearchParams(window.location.search).get('token') || '';
  const navigate = useNavigate();
  const { isAuthenticated, refreshAuth } = useAuth();

  // Invitation info
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state (for new account)
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch invitation info on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setIsLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/invitations/${encodeURIComponent(token)}`, {
          credentials: 'include',
        });

        const result = await response.json();

        if (!result.success) {
          if (result.code === 'INVITATION_EXPIRED') {
            setError('This invitation has expired. Please ask for a new invitation.');
          } else if (result.code === 'INVITATION_INVALID') {
            setError('This invitation is invalid or has already been used.');
          } else {
            setError(result.error || 'Failed to load invitation');
          }
          return;
        }

        setInvitation(result.invitation);
      } catch {
        setError('An error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const getPasswordStrength = useCallback((pwd: string) => {
    const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(pwd));
    return {
      score: passed.length,
      total: PASSWORD_REQUIREMENTS.length,
      requirements: PASSWORD_REQUIREMENTS.map(req => ({
        ...req,
        met: req.test(pwd),
      })),
    };
  }, []);

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;

  // Accept invitation for existing logged-in user
  const handleAcceptAsLoggedInUser = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to accept invitation');
        return;
      }

      await refreshAuth();
      if (result.redirectUrl) window.location.assign(result.redirectUrl);
      else navigate('/');
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [token, navigate, refreshAuth]);

  // Accept invitation for existing user (needs to sign in)
  const handleSignInToAccept = useCallback(() => {
    // Redirect to login with return URL
    navigate(`/login?redirect=${encodeURIComponent(`/invite/${encodeURIComponent(token)}`)}`);
  }, [token, navigate]);

  // Accept invitation and create new account
  const handleCreateAccountAndAccept = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (passwordStrength.score < PASSWORD_REQUIREMENTS.length) {
      setError('Password does not meet requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          password,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to accept invitation');
        return;
      }

      await refreshAuth();
      if (result.redirectUrl) window.location.assign(result.redirectUrl);
      else navigate('/');
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [token, name, password, passwordStrength, passwordsMatch, navigate, refreshAuth]);

  const handleGoogleSignIn = () => {
    // Sign in, then return here to confirm joining the company.
    window.location.href = `${API_URL}/api/auth/google?invite=${encodeURIComponent(token)}`;
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f6f6f7',
        gap: '16px',
      }}>
        <Spinner size="large" />
        <Text as="p" variant="bodyMd" tone="subdued">Loading invitation...</Text>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f6f6f7',
      padding: '20px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>
        <BlockStack gap="600">
          {/* Logo/Brand */}
          <Box paddingBlockEnd="400">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                Whenly
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Delivery Date & Warehouse Management
              </Text>
            </BlockStack>
          </Box>

          {/* Error State */}
          {error && !invitation ? (
            <Card>
              <BlockStack gap="400">
                <Banner tone="critical">
                  {error}
                </Banner>
                <Button onClick={() => navigate('/login')} variant="primary" fullWidth>
                  Go to sign in
                </Button>
              </BlockStack>
            </Card>
          ) : invitation && (
            <>
              {/* Error Banner */}
              {error && (
                <Banner tone="critical" onDismiss={() => setError(null)}>
                  {error}
                </Banner>
              )}

              {/* Invitation Info Card */}
              <Card>
                <BlockStack gap="500">
                  <BlockStack gap="200">
                    <Text as="h2" variant="headingMd">
                      You've been invited!
                    </Text>
                    <Text as="p" variant="bodyMd">
                      <strong>{invitation.invitedBy}</strong> has invited you to join{' '}
                      <strong>{invitation.organizationName}</strong> as a{' '}
                      <strong>{invitation.role}</strong>.
                    </Text>
                  </BlockStack>

                  <Divider />

                  {/* Already logged in */}
                  {isAuthenticated ? (
                    <BlockStack gap="400">
                      <Text as="p" variant="bodyMd">
                        You're signed in. Click below to accept the invitation.
                      </Text>
                      <Button
                        onClick={handleAcceptAsLoggedInUser}
                        variant="primary"
                        size="large"
                        fullWidth
                        loading={isSubmitting}
                      >
                        Accept invitation
                      </Button>
                    </BlockStack>
                  ) : invitation.hasExistingAccount ? (
                    /* Existing account - needs to sign in */
                    <BlockStack gap="400">
                      <Text as="p" variant="bodyMd">
                        An account exists with <strong>{invitation.email}</strong>.
                        Please sign in to accept the invitation.
                      </Text>
                      <Button
                        onClick={handleSignInToAccept}
                        variant="primary"
                        size="large"
                        fullWidth
                      >
                        Sign in to accept
                      </Button>
                      <InlineStack gap="400" align="center" blockAlign="center">
                        <div style={{ flex: 1 }}><Divider /></div>
                        <Text as="span" variant="bodySm" tone="subdued">or</Text>
                        <div style={{ flex: 1 }}><Divider /></div>
                      </InlineStack>
                      <button
                        onClick={handleGoogleSignIn}
                        type="button"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px',
                          width: '100%',
                          padding: '12px 24px',
                          fontSize: '15px',
                          fontWeight: 500,
                          backgroundColor: '#fff',
                          border: '1px solid #c4cdd5',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s, border-color 0.2s',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#f6f6f7';
                          e.currentTarget.style.borderColor = '#8c9196';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#c4cdd5';
                        }}
                      >
                        <GoogleIcon />
                        <span>Continue with Google</span>
                      </button>
                    </BlockStack>
                  ) : (
                    /* New account - create and accept */
                    <BlockStack gap="400">
                      <Text as="p" variant="bodyMd">
                        Create an account to accept the invitation.
                        Your account will be created with <strong>{invitation.email}</strong>.
                      </Text>

                      <Form onSubmit={handleCreateAccountAndAccept}>
                        <BlockStack gap="400">
                          <TextField
                            label="Your name"
                            type="text"
                            requiredIndicator value={name}
                            onChange={setName}
                            autoComplete="name"
                            disabled={isSubmitting}
                          />
                          <TextField
                            label="Password"
                            type="password"
                            requiredIndicator value={password}
                            onChange={setPassword}
                            autoComplete="new-password"
                            disabled={isSubmitting}
                            helpText={
                              password.length > 0 ? (
                                <BlockStack gap="100">
                                  {passwordStrength.requirements.map((req, i) => (
                                    <Text
                                      key={i}
                                      as="span"
                                      variant="bodySm"
                                      tone={req.met ? 'success' : 'subdued'}
                                    >
                                      {req.met ? '✓' : '○'} {req.message}
                                    </Text>
                                  ))}
                                </BlockStack>
                              ) : undefined
                            }
                          />
                          <TextField
                            label="Confirm password"
                            type="password"
                            requiredIndicator value={confirmPassword}
                            onChange={setConfirmPassword}
                            autoComplete="new-password"
                            disabled={isSubmitting}
                            error={
                              confirmPassword && !passwordsMatch
                                ? 'Passwords do not match'
                                : undefined
                            }
                          />
                          <Button icon={PlusIcon}
                            submit
                            variant="primary"
                            size="large"
                            fullWidth
                            loading={isSubmitting}
                            disabled={
                              !name ||
                              passwordStrength.score < PASSWORD_REQUIREMENTS.length ||
                              !passwordsMatch
                            }
                          >
                            Create account & join
                          </Button>
                        </BlockStack>
                      </Form>

                      <InlineStack gap="400" align="center" blockAlign="center">
                        <div style={{ flex: 1 }}><Divider /></div>
                        <Text as="span" variant="bodySm" tone="subdued">or</Text>
                        <div style={{ flex: 1 }}><Divider /></div>
                      </InlineStack>

                      <button
                        onClick={handleGoogleSignIn}
                        type="button"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '12px',
                          width: '100%',
                          padding: '12px 24px',
                          fontSize: '15px',
                          fontWeight: 500,
                          backgroundColor: '#fff',
                          border: '1px solid #c4cdd5',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s, border-color 0.2s',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#f6f6f7';
                          e.currentTarget.style.borderColor = '#8c9196';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#c4cdd5';
                        }}
                      >
                        <GoogleIcon />
                        <span>Continue with Google</span>
                      </button>
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </>
          )}

          {/* Help Text */}
          <Text as="p" variant="bodySm" tone="subdued" alignment="center">
            Need help? Contact{' '}
            <a href="mailto:support@whenly.app" style={{ color: '#2C6ECB' }}>
              support@whenly.app
            </a>
          </Text>
        </BlockStack>
      </div>
    </div>
  );
}

// Google Icon Component
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
